import { useQuery } from '@tanstack/react-query';
import { useId, useMemo, useState, type ReactElement, type RefObject } from 'react';
import type { ConversationItem } from '@chatapp-inbox/shared';
import { listUsers } from '../../api';
import { usersKey } from '../conversation';
import { Button } from './Button';
import { apiErrorMessage } from './error-message';
import { useFocusRescue } from './use-focus-rescue';
import styles from './AdminAssignControls.module.css';

interface AdminAssignControlsProps {
  conversation: ConversationItem;
  viewerId: string;
  /** Uma atribuição está em andamento: os botões ignoram cliques até ela terminar. */
  busy: boolean;
  onAssign: (userId: string, name: string) => void;
  onClaim: () => void;
  onRelease: () => void;
  /** O pai leva o foco para cá quando a ação termina e o botão clicado deixou de existir. */
  selectRef: RefObject<HTMLSelectElement | null>;
}

interface Choice {
  id: string;
  name: string;
}

const byName = (a: Choice, b: Choice): number => a.name.localeCompare(b.name, 'pt-BR');

/**
 * Controles do administrador: atribuir a qualquer usuário ativo, assumir para si e liberar. A lista
 * de usuários só é pedida aqui (`GET /admin/users` responde 403 a atendente). Se ela falhar, os
 * botões "Assumir para mim" e "Liberar" continuam à mão: só o seletor é substituído pelo erro.
 */
export function AdminAssignControls({
  conversation,
  viewerId,
  busy,
  onAssign,
  onClaim,
  onRelease,
  selectRef,
}: AdminAssignControlsProps): ReactElement {
  const selectId = useId();
  const users = useQuery({ queryKey: usersKey, queryFn: ({ signal }) => listUsers(signal) });
  const currentId = conversation.assignedTo?.id ?? null;
  const currentName = conversation.assignedTo?.name ?? null;

  // Pessoa escolhida no seletor ainda não aplicada (null = a pessoa não mexeu). Qualquer mudança
  // de responsável (por mim, por outra aba ou pelo tempo real) descarta a escolha velha: o seletor
  // volta a mostrar quem é o responsável de verdade.
  const [picked, setPicked] = useState<string | null>(null);
  const [seenCurrentId, setSeenCurrentId] = useState(currentId);
  if (seenCurrentId !== currentId) {
    setSeenCurrentId(currentId);
    setPicked(null);
  }

  // Só usuários ATIVOS, por nome. O responsável atual entra mesmo que a lista ainda não tenha
  // chegado (ou o tenha desativado nesse meio-tempo): o seletor sempre consegue mostrá-lo.
  const choices = useMemo(() => {
    const list: Choice[] = (users.data ?? [])
      .filter((user) => user.active)
      .map(({ id, name }) => ({ id, name }));
    if (currentId && currentName && !list.some((c) => c.id === currentId)) {
      list.push({ id: currentId, name: currentName });
    }
    return list.sort(byName);
  }, [users.data, currentId, currentName]);

  const value = picked ?? currentId ?? '';
  const unchanged = value === '' || value === currentId;

  // "Tentar de novo" some quando a lista chega (o seletor ocupa o lugar) e levaria o foco ao <body>.
  const rescue = useFocusRescue(users.data !== undefined, selectRef);
  const retry = (): void => {
    rescue.arm();
    void users.refetch().then((result) => {
      if (result.isError) rescue.disarm();
    });
  };

  const chosenName = choices.find((c) => c.id === value)?.name ?? '';
  const loadFailed = users.isError && !users.data;

  return (
    <div className={styles.controls}>
      {loadFailed ? (
        <div className={styles.loadError}>
          <p role="alert" className={styles.loadErrorText}>
            Não foi possível carregar as pessoas. {apiErrorMessage(users.error, 'Tente de novo.')}
          </p>
          <Button busy={users.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      ) : (
        <div className={styles.field}>
          <label htmlFor={selectId} className={styles.label}>
            Atribuir a
          </label>
          <select
            id={selectId}
            ref={selectRef}
            className={styles.select}
            value={value}
            disabled={users.isPending}
            onChange={(event) => setPicked(event.target.value)}
          >
            {users.isPending && <option value="">Carregando pessoas…</option>}
            {!users.isPending && currentId === null && (
              <option value="" disabled>
                Escolha uma pessoa
              </option>
            )}
            {choices.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.id === currentId ? `${choice.name} (atual)` : choice.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className={styles.actions}>
        {!loadFailed && (
          <Button
            variant="primary"
            busy={busy}
            disabled={unchanged || users.isPending}
            onClick={() => onAssign(value, chosenName)}
          >
            Atribuir
          </Button>
        )}
        {currentId !== viewerId && (
          <Button busy={busy} onClick={onClaim}>
            Assumir para mim
          </Button>
        )}
        {currentId !== null && (
          <Button busy={busy} onClick={onRelease}>
            Liberar
          </Button>
        )}
      </div>
    </div>
  );
}
